import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ADMIN_GROUPS } from "@/config/adminMenu";

// Paletta gradientów per kafel — rotujemy w obrębie grupy dla efektu jak w giordano.pl.
const TILE_GRADIENTS = [
  "from-rose-500 to-pink-600",
  "from-orange-500 to-red-500",
  "from-amber-500 to-orange-500",
  "from-yellow-500 to-amber-500",
  "from-emerald-500 to-teal-500",
  "from-green-500 to-emerald-500",
  "from-sky-500 to-blue-500",
  "from-blue-500 to-indigo-600",
  "from-indigo-500 to-purple-600",
  "from-purple-500 to-pink-500",
  "from-fuchsia-500 to-purple-600",
  "from-pink-500 to-rose-500",
];

// Hero header per grupa
const HERO_GRADIENTS = {
  blue: "from-blue-500 via-blue-600 to-indigo-600",
  emerald: "from-emerald-500 via-teal-500 to-cyan-600",
  amber: "from-amber-500 via-orange-500 to-red-500",
  purple: "from-fuchsia-500 via-purple-600 to-indigo-600",
  rose: "from-rose-500 via-pink-500 to-fuchsia-500",
  sky: "from-sky-500 via-blue-500 to-indigo-500",
  slate: "from-slate-500 via-slate-600 to-slate-700",
};

export default function ToolsShortcuts() {
  const [expanded, setExpanded] = useState(new Set(ADMIN_GROUPS.map((g) => g.key)));

  const toggle = (key) => {
    const s = new Set(expanded);
    if (s.has(key)) s.delete(key); else s.add(key);
    setExpanded(s);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold">Narzędzia</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Klik w kafelek otwiera moduł. Sekcje można zwijać.
          </p>
        </div>
        <div className="flex gap-3 text-xs">
          <button onClick={() => setExpanded(new Set(ADMIN_GROUPS.map((g) => g.key)))}
                  className="text-muted-foreground hover:text-foreground">
            Rozwiń wszystko
          </button>
          <span className="text-muted-foreground/40">·</span>
          <button onClick={() => setExpanded(new Set())}
                  className="text-muted-foreground hover:text-foreground">
            Zwiń wszystko
          </button>
        </div>
      </div>

      {ADMIN_GROUPS.map((group) => {
        const isExpanded = expanded.has(group.key);
        const heroGrad = HERO_GRADIENTS[group.color] || HERO_GRADIENTS.slate;
        const GroupIcon = group.icon;
        return (
          <section key={group.key} className="space-y-4">
            {/* Hero header sekcji */}
            <button
              onClick={() => toggle(group.key)}
              className={`w-full bg-gradient-to-r ${heroGrad} rounded-2xl p-5 md:p-6 shadow-lg hover:shadow-xl transition-all group`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <GroupIcon className="w-6 h-6 text-white" strokeWidth={2} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-lg md:text-xl font-heading font-bold text-white">
                    {group.label}
                  </h3>
                  <p className="text-xs md:text-sm text-white/80 mt-0.5">
                    {group.items.length} {group.items.length === 1 ? "narzędzie" : group.items.length < 5 ? "narzędzia" : "narzędzi"} · kliknij aby {isExpanded ? "zwinąć" : "rozwinąć"}
                  </p>
                </div>
                <div className="text-white/90">
                  {isExpanded ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
                </div>
              </div>
            </button>

            {/* Kafle */}
            {isExpanded && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {group.items.map((item, idx) => {
                  const gradient = TILE_GRADIENTS[idx % TILE_GRADIENTS.length];
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`group relative overflow-hidden bg-gradient-to-br ${gradient} rounded-2xl p-4 md:p-5 shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 min-h-[130px] flex flex-col`}
                    >
                      {/* Ikona */}
                      <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                      </div>
                      {/* Tytul */}
                      <h4 className="font-heading font-bold text-white text-sm md:text-base leading-tight">
                        {item.label}
                      </h4>
                      {/* Opis */}
                      {item.desc && (
                        <p className="text-[11px] md:text-xs text-white/85 mt-1.5 leading-snug line-clamp-3">
                          {item.desc}
                        </p>
                      )}
                      {/* Decoration */}
                      <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors" />
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
